import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
  Form,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  UserOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import {
  fetchTransactionRecords,
  fetchTransactionTypes,
  setFilters,
  setPagination,
  clearError,
} from '../../features/finance/financeSlice';
import { formatDateTime, formatAmount, formatAmountWithRegion, maskPhone, getStatusColor, getStatusText } from '../../utils/index';
import { get } from '../../utils/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TransactionRecords = () => {
  const dispatch = useDispatch();
  const { transactionRecords, transactionTypes, pagination, filters, isLoading, error } = useSelector((state) => state.finance);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // 获取流水类型列表
  useEffect(() => {
    dispatch(fetchTransactionTypes());
  }, [dispatch]);

  // 获取地区列表
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await get('/admin/regions/all');
        if (res.code === 200) {
          const regionList = res.data || [];
          setRegions(regionList);
          // 默认选择中国地区（CN）
          const defaultRegion = regionList.find(r => 
            (r.region_code || r.regionCode) === 'ZMW'
          );
          if (defaultRegion) {
            setSelectedRegion(defaultRegion);
          }
        }
      } catch (error) {
        console.error('获取地区列表失败:', error);
      }
    };
    fetchRegions();
  }, []);

  // 使用 useMemo 计算请求参数
  const requestParams = React.useMemo(() => ({
    ...filters,
    page: pagination.current || 1,
    limit: pagination.pageSize || 20,
  }), [filters, pagination.current, pagination.pageSize]);
  
  // 使用 useRef 防止死循环
  const prevParamsRef = React.useRef('');
  const isLoadingRef = React.useRef(false);
  
  useEffect(() => {
    // 如果正在加载，则不重复请求
    if (isLoadingRef.current) {
      return;
    }
    
    // 深度比较参数，只有真正变化时才请求
    const paramsStr = JSON.stringify(requestParams);
    
    if (paramsStr !== prevParamsRef.current) {
      prevParamsRef.current = paramsStr;
      isLoadingRef.current = true;
      dispatch(fetchTransactionRecords(requestParams)).finally(() => {
        isLoadingRef.current = false;
      });
    }
  }, [dispatch, requestParams]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '流水ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户信息',
      dataIndex: 'userName',
      key: 'userName',
      render: (name, record) => (
        <Space>
          <UserOutlined />
          <span>{name}</span>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
            ({record.userPhone})
          </span>
        </Space>
      ),
    },
    {
      title: '流水类型',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type, record) => {
        const typeColors = {
          recharge: 'green',
          withdraw: 'red',
          trade: 'blue',
          commission: 'purple',
          activity: 'gold',
          adjustment: 'cyan',
          balance_freeze: 'orange',
        };
        const color = typeColors[type] || 'default';
        const text = record.transactionTypeName || type;
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '交易金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => {
        const isPositive = amount>0?true:false
        const displayAmount = selectedRegion ? formatAmountWithRegion(Math.abs(amount), selectedRegion) : formatAmount(Math.abs(amount));
        return (
          <Space direction="vertical" size={0}>
            <Space>
              {isPositive ? (
                <RiseOutlined style={{ color: '#52c41a' }} />
              ) : (
                <FallOutlined style={{ color: '#ff4d4f' }} />
              )}
              <span style={{ 
                fontWeight: 'bold', 
                color: isPositive ? '#52c41a' : '#ff4d4f' 
              }}>
                {isPositive ? '+' : '-'}{displayAmount}
              </span>
            </Space>
            {/* {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                原始: {isPositive ? '+' : '-'}{formatAmount(Math.abs(amount))}
              </span>
            )} */}
          </Space>
        );
      },
    },
    {
      title: '余额变化',
      dataIndex: 'balanceChange',
      key: 'balanceChange',
      render: (_, record) => {
        const beforeDisplay = selectedRegion ? formatAmountWithRegion(record.balanceBefore, selectedRegion) : formatAmount(record.balanceBefore);
        const afterDisplay = selectedRegion ? formatAmountWithRegion(record.balanceAfter, selectedRegion) : formatAmount(record.balanceAfter);
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <DollarOutlined />
              <span style={{ color: '#8c8c8c' }}>
                {beforeDisplay} → {afterDisplay}
              </span>
            </Space>
            {/* {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                原始: {formatAmount(record.balanceBefore)} → {formatAmount(record.balanceAfter)}
              </span>
            )} */}
          </Space>
        );
      },
    },
    {
      title: '关联订单',
      dataIndex: 'relatedOrderId',
      key: 'relatedOrderId',
      render: (orderId) => orderId || '-',
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '交易时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  // 导出流水
  const handleExport = async () => {
    try {
      // TODO: 实现导出功能
      message.success('导出功能待实现');
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">资金流水</h1>
        <p className="page-description">查看所有资金变动记录</p>
      </div>

      <Card>
        {/* 地区选择器 */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px' ,display:'none' }}>
          <GlobalOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
          <span style={{ fontWeight: 500 }}>显示货币：</span>
          <Select
            style={{ width: 360 }}
            value={selectedRegion ? (selectedRegion.region_code || selectedRegion.regionCode) : undefined}
            onChange={(value) => {
              const region = regions.find(r => 
                (r.region_code || r.regionCode) === value
              );
              setSelectedRegion(region);
            }}
            placeholder="选择地区"
          >
            {regions.map(region => {
              const regionCode = region.region_code || region.regionCode;
              const regionName = region.region_name || region.regionName;
              const currencySymbol = region.currency_symbol || region.currencySymbol;
              const currencyName = region.currency_name || region.currencyName;
              const exchangeRate = region.exchange_rate || region.exchangeRate || 1;
              
              return (
                <Option key={regionCode} value={regionCode}>
                  <Space>
                    <Tag color="cyan">{regionCode}</Tag>
                    <span>{regionName}</span>
                    <Tag color="green">{currencySymbol} {currencyName}</Tag>
                    {exchangeRate !== 1 && (
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        (汇率: {exchangeRate})
                      </span>
                    )}
                  </Space>
                </Option>
              );
            })}
          </Select>
          {selectedRegion && (
            <Tag color="blue">
              当前: {selectedRegion.currency_symbol || selectedRegion.currencySymbol} {selectedRegion.currency_name || selectedRegion.currencyName}
            </Tag>
          )}
        </div>

        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索用户手机号/姓名"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="transactionType">
            <Select placeholder="流水类型" style={{ width: 150 }} allowClear>
              {transactionTypes.map((type) => (
                <Option key={type.code} value={type.code}>
                  {type.nameCn}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="交易状态" style={{ width: 120 }} allowClear>
              <Option value="success">成功</Option>
              <Option value="pending">处理中</Option>
              <Option value="failed">失败</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                导出
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            <Button
              disabled={selectedRowKeys.length === 0}
              icon={<DownloadOutlined />}
            >
              批量导出
            </Button>
          </Space>
          <div>
            共 {pagination.total} 条流水记录
          </div>
        </div>

        {/* 流水记录表格 */}
        <Table
          columns={columns}
          dataSource={transactionRecords}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 流水详情抽屉 */}
      <Drawer
        title="流水详情"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Statistic
                  title="交易金额"
                  value={selectedRecord.amount}
                  prefix={<DollarOutlined />}
                  formatter={(value) => selectedRegion ? formatAmountWithRegion(value, selectedRegion) : formatAmount(value)}
                />
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    原始: {formatAmount(selectedRecord.amount)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic
                  title="交易状态"
                  value={getStatusText(selectedRecord.status)}
                />
              </Col>
            </Row>

            <Descriptions
              title="流水信息"
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="流水ID">
                {selectedRecord.id}
              </Descriptions.Item>
              <Descriptions.Item label="用户信息">
                {selectedRecord.userName} ({maskPhone(selectedRecord.userPhone)})
              </Descriptions.Item>
              <Descriptions.Item label="流水类型">
                <Tag color="blue">{selectedRecord.transactionType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="交易金额">
                <Space direction="vertical" size={0}>
                  <span>{selectedRegion ? formatAmountWithRegion(selectedRecord.amount, selectedRegion) : formatAmount(selectedRecord.amount)}</span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.amount)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="余额变化">
                <Space direction="vertical" size={0}>
                  <span>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.balanceBefore, selectedRegion) : formatAmount(selectedRecord.balanceBefore)} 
                    {' → '}
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.balanceAfter, selectedRegion) : formatAmount(selectedRecord.balanceAfter)}
                  </span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.balanceBefore)} → {formatAmount(selectedRecord.balanceAfter)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="关联订单">
                {selectedRecord.relatedOrderId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="交易状态">
                <Tag color={getStatusColor(selectedRecord.status)}>
                  {getStatusText(selectedRecord.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="交易时间">
                {formatDateTime(selectedRecord.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="备注">
                {selectedRecord.description || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default TransactionRecords;
